import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  Switch,
  Typography,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import DeleteIcon from "@mui/icons-material/Delete";
import DoneIcon from "@mui/icons-material/Done";
import UndoIcon from "@mui/icons-material/Undo";
import DashboardLayout from "common/LayoutContainers/DashboardLayout";
import PageHeader from "common/PageHeader";
import Footer from "common/Footer";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useCompany } from "context/CompanyContext";
import { useLanguage } from "context/LanguageContext";
import { useSnackbar } from "context/SnackbarContext";
import api from "services/api";
import { buildPdfSplitGroups, mergeWithNextFromDocuments } from "./pdfSplitGroups";

const PdfSplitPage = () => {
  const { selectedCompany } = useCompany();
  const { t } = useLanguage();
  const showSnackbar = useSnackbar();
  const [jobs, setJobs] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [jobDetail, setJobDetail] = useState(null);
  const [mergeWithNextByPage, setMergeWithNextByPage] = useState({});
  const [removedPages, setRemovedPages] = useState({});
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [importing, setImporting] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [splitHistory, setSplitHistory] = useState([]);
  const [discardTarget, setDiscardTarget] = useState(null);
  const [previewPage, setPreviewPage] = useState(null);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  // Job shown right now: a suggestion that arrives after switching jobs is dropped.
  const shownJobIdRef = useRef(null);

  const loadJobs = async () => {
    if (!selectedCompany?.id) return;
    setLoadingJobs(true);
    try {
      const response = await api.get("/api/pdf-split-jobs/", {
        params: { company_id: selectedCompany.id, status: "pending" },
      });
      const nextJobs = response.data.jobs || [];
      setJobs(nextJobs);
      if (!selectedJobId && nextJobs.length > 0) {
        setSelectedJobId(nextJobs[0].id);
      }
    } catch (error) {
      showSnackbar(error.response?.data?.error || t("pdfSplitLoadFailed"), "error");
    } finally {
      setLoadingJobs(false);
    }
  };

  const loadJobDetail = async (jobId) => {
    if (!jobId) return;
    setLoadingDetail(true);
    try {
      const response = await api.get(`/api/pdf-split-jobs/${jobId}/`);
      const job = response.data.job;
      shownJobIdRef.current = job.id;
      setJobDetail(job);
      setMergeWithNextByPage({});
      setRemovedPages({});
      setSplitHistory([]);
      setSuggestion(null);
    } catch (error) {
      showSnackbar(error.response?.data?.error || t("pdfSplitLoadFailed"), "error");
    } finally {
      setLoadingDetail(false);
    }
  };

  useEffect(() => {
    setJobs([]);
    setSelectedJobId(null);
    setJobDetail(null);
    loadJobs();
  }, [selectedCompany?.id]);

  useEffect(() => {
    if (selectedJobId) loadJobDetail(selectedJobId);
  }, [selectedJobId]);

  const groups = useMemo(
    () => buildPdfSplitGroups(jobDetail?.pages, mergeWithNextByPage, removedPages),
    [jobDetail?.pages, mergeWithNextByPage, removedPages]
  );

  const lastIncludedPageNumber = useMemo(() => {
    const includedPages = (jobDetail?.pages || []).filter(
      (page) => !removedPages[page.page_number]
    );
    return includedPages.length ? includedPages[includedPages.length - 1].page_number : null;
  }, [jobDetail?.pages, removedPages]);

  const documentNumberByPage = useMemo(() => {
    const mapped = {};
    groups.forEach((group, index) => {
      group.forEach((pageNumber) => {
        mapped[pageNumber] = index + 1;
      });
    });
    return mapped;
  }, [groups]);

  const includedPageCount = useMemo(
    () => (jobDetail?.pages || []).filter((page) => !removedPages[page.page_number]).length,
    [jobDetail?.pages, removedPages]
  );

  const setEachPageSeparate = () => {
    saveCurrentLayout();
    setMergeWithNextByPage({});
    setRemovedPages({});
  };

  const setAllPagesOneGroup = () => {
    saveCurrentLayout();
    setMergeWithNextByPage(
      (jobDetail?.pages || []).reduce((acc, page, index, pages) => {
        acc[page.page_number] = index < pages.length - 1;
        return acc;
      }, {})
    );
    setRemovedPages({});
  };

  const saveCurrentLayout = () => {
    setSplitHistory((previous) => [...previous.slice(-9), { mergeWithNextByPage, removedPages }]);
  };

  const undoLastSplitChange = () => {
    const lastChange = splitHistory[splitHistory.length - 1];
    if (!lastChange) return;
    setMergeWithNextByPage(lastChange.mergeWithNextByPage);
    setRemovedPages(lastChange.removedPages);
    setSplitHistory((previous) => previous.slice(0, -1));
  };

  const handleSuggest = async () => {
    if (!jobDetail?.id) return;
    const jobId = jobDetail.id;
    setSuggesting(true);
    try {
      const response = await api.post(`/api/pdf-split-jobs/${jobId}/suggest/`);
      const result = response.data.suggestion;
      if (shownJobIdRef.current !== jobId) return;
      setSuggestion(result);
      if (result.documents.length > 0) {
        saveCurrentLayout();
        setMergeWithNextByPage(mergeWithNextFromDocuments(result.documents));
        setRemovedPages({});
      }
    } catch (error) {
      showSnackbar(error.response?.data?.error || t("pdfSplitSuggestFailed"), "error");
    } finally {
      setSuggesting(false);
    }
  };

  const lowConfidencePages = (suggestion?.documents || [])
    .filter((document) => document.confidence === "low")
    .map((document) =>
      document.first_page === document.last_page
        ? `${document.first_page}`
        : `${document.first_page}–${document.last_page}`
    );

  const handleImport = async () => {
    if (!jobDetail?.id || groups.length === 0) {
      showSnackbar(t("pdfSplitNoGroups"), "error");
      return;
    }
    setImporting(true);
    try {
      await api.post(`/api/pdf-split-jobs/${jobDetail.id}/import/`, { groups });
      showSnackbar(t("pdfSplitImported"), "success");
      setJobDetail(null);
      setSelectedJobId(null);
      window.dispatchEvent(new Event("pdfSplitJobsChanged"));
      await loadJobs();
    } catch (error) {
      showSnackbar(error.response?.data?.error || t("pdfSplitImportFailed"), "error");
    } finally {
      setImporting(false);
    }
  };

  const handleDiscard = async () => {
    if (!discardTarget?.jobId) return;
    if (selectedCompany?.id !== discardTarget.companyId) {
      showSnackbar(
        t("pdfSplitCompanyChanged") || "Company changed. Reopen the job before discarding it.",
        "error"
      );
      setDiscardTarget(null);
      return;
    }
    setDiscarding(true);
    try {
      await api.post(`/api/pdf-split-jobs/${discardTarget.jobId}/cancel/`);
      showSnackbar(t("pdfSplitDiscarded"), "success");
      setJobDetail(null);
      setSelectedJobId(null);
      setDiscardTarget(null);
      window.dispatchEvent(new Event("pdfSplitJobsChanged"));
      await loadJobs();
    } catch (error) {
      showSnackbar(error.response?.data?.error || t("pdfSplitDiscardFailed"), "error");
    } finally {
      setDiscarding(false);
    }
  };

  return (
    <DashboardLayout>
      <MDBox pt={3} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card
              sx={{
                overflow: "hidden",
                border: "1px solid",
                borderColor: "divider",
                boxShadow: "none",
              }}
            >
              <PageHeader title={t("pdfSplitQueue")} icon={<CallSplitIcon color="info" />} />

              <MDBox p={3}>
                {!selectedCompany?.id && <Alert severity="info">{t("selectCompanyFirst")}</Alert>}

                {selectedCompany?.id && (
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={3}>
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                        <Typography variant="subtitle2">{t("pendingPdfSplits")}</Typography>
                        {loadingJobs && <CircularProgress size={18} />}
                      </Box>
                      <Card variant="outlined" sx={{ maxHeight: 620, overflow: "auto" }}>
                        {jobs.length === 0 && !loadingJobs ? (
                          <Box p={2}>
                            <Typography variant="body2" color="text.secondary">
                              {t("noPendingPdfSplits")}
                            </Typography>
                          </Box>
                        ) : (
                          <List dense disablePadding>
                            {jobs.map((job) => (
                              <ListItemButton
                                key={job.id}
                                selected={selectedJobId === job.id}
                                onClick={() => setSelectedJobId(job.id)}
                              >
                                <ListItemText
                                  primary={job.original_filename}
                                  secondary={`${job.page_count} ${t("pages") || "psl."} · ${
                                    job.sender || "-"
                                  }`}
                                />
                              </ListItemButton>
                            ))}
                          </List>
                        )}
                      </Card>
                    </Grid>

                    <Grid item xs={12} md={9}>
                      {loadingDetail ? (
                        <Box display="flex" justifyContent="center" py={8}>
                          <CircularProgress />
                        </Box>
                      ) : jobDetail ? (
                        <Box>
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            gap={2}
                            flexWrap="wrap"
                            mb={2}
                          >
                            <Box>
                              <Typography variant="h6">{jobDetail.original_filename}</Typography>
                              <Typography variant="body2" color="text.secondary">
                                {jobDetail.subject || "-"}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {jobDetail.sender || "-"}
                              </Typography>
                            </Box>
                            <Box display="flex" gap={1} flexWrap="wrap">
                              {jobDetail.is_advance_report && (
                                <Chip size="small" label={t("advanceReport")} color="warning" />
                              )}
                              <Chip
                                size="small"
                                color="info"
                                variant="outlined"
                                label={`${t("documents")}: ${groups.length}`}
                              />
                              <Chip
                                size="small"
                                variant="outlined"
                                label={`${t("pages") || "Psl."}: ${includedPageCount}`}
                              />
                            </Box>
                          </Box>

                          <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                            <MDButton
                              variant="outlined"
                              color="info"
                              startIcon={suggesting ? null : <AutoAwesomeIcon />}
                              onClick={handleSuggest}
                              disabled={suggesting || importing || discarding}
                            >
                              {suggesting ? (
                                <CircularProgress size={18} color="inherit" />
                              ) : (
                                t("pdfSplitSuggest")
                              )}
                            </MDButton>
                            <MDButton
                              variant="outlined"
                              color="secondary"
                              onClick={setEachPageSeparate}
                            >
                              {t("pdfSplitEachPage")}
                            </MDButton>
                            <MDButton
                              variant="outlined"
                              color="secondary"
                              onClick={setAllPagesOneGroup}
                            >
                              {t("pdfSplitAllPages")}
                            </MDButton>
                            <MDButton
                              variant="outlined"
                              color="secondary"
                              startIcon={<UndoIcon />}
                              onClick={undoLastSplitChange}
                              disabled={splitHistory.length === 0}
                            >
                              {t("undo") || "Atšaukti paskutinį"}
                            </MDButton>
                            <MDButton
                              variant="contained"
                              color="info"
                              startIcon={importing ? null : <DoneIcon />}
                              onClick={handleImport}
                              aria-label={t("pdfSplitImportGroups")}
                              disabled={importing || discarding || groups.length === 0}
                            >
                              {importing ? (
                                <CircularProgress size={18} color="inherit" />
                              ) : (
                                t("pdfSplitImportGroups")
                              )}
                            </MDButton>
                            <MDButton
                              variant="outlined"
                              color="error"
                              startIcon={discarding ? null : <DeleteIcon />}
                              onClick={() =>
                                setDiscardTarget({
                                  jobId: jobDetail.id,
                                  companyId: selectedCompany?.id,
                                })
                              }
                              disabled={importing || discarding}
                              aria-label={t("pdfSplitDiscard")}
                            >
                              {discarding ? (
                                <CircularProgress size={18} color="inherit" />
                              ) : (
                                t("pdfSplitDiscard")
                              )}
                            </MDButton>
                          </Box>

                          {suggestion && (
                            <Alert
                              severity={
                                suggestion.documents.length === 0 || lowConfidencePages.length > 0
                                  ? "warning"
                                  : "success"
                              }
                              sx={{ mb: 2 }}
                            >
                              {suggestion.documents.length === 0
                                ? t("pdfSplitSuggestInvalid")
                                : t("pdfSplitSuggested").replace(
                                    "{count}",
                                    suggestion.documents.length
                                  )}
                              {lowConfidencePages.length > 0 && (
                                <div>
                                  {t("pdfSplitSuggestLowConfidence").replace(
                                    "{pages}",
                                    lowConfidencePages.join(", ")
                                  )}
                                </div>
                              )}
                              {suggestion.notes && <div>{suggestion.notes}</div>}
                            </Alert>
                          )}

                          <Alert severity="info" sx={{ mb: 2 }}>
                            {t("pdfSplitInstructions")}
                          </Alert>

                          <Box
                            sx={{
                              mb: 2,
                              p: 1.5,
                              border: "1px solid",
                              borderColor: "divider",
                              borderRadius: 1.5,
                              backgroundColor: "grey.50",
                            }}
                          >
                            <Typography variant="subtitle2" fontWeight={600} mb={1}>
                              {t("documents")}: {groups.length}
                            </Typography>
                            <Box display="flex" gap={0.75} flexWrap="wrap">
                              {groups.map((group, index) => (
                                <Chip
                                  key={group.join("-")}
                                  size="small"
                                  color="info"
                                  variant="outlined"
                                  label={`${t("document")} ${index + 1}: ${
                                    t("page") || "Psl."
                                  } ${group.join(", ")}`}
                                />
                              ))}
                            </Box>
                          </Box>

                          <Grid container spacing={1.5}>
                            {(jobDetail.pages || []).map((page) => {
                              const removed = !!removedPages[page.page_number];
                              const documentNumber = documentNumberByPage[page.page_number];
                              const mergesWithNext = !!mergeWithNextByPage[page.page_number];
                              const canMergeWithNext = page.page_number !== lastIncludedPageNumber;
                              const currentGroup = groups[(documentNumber || 1) - 1] || [];
                              const isGroupStart = currentGroup[0] === page.page_number;
                              const isGroupEnd =
                                currentGroup[currentGroup.length - 1] === page.page_number;
                              return (
                                <Grid item xs={12} sm={6} md={4} lg={3} key={page.id}>
                                  <Card
                                    variant="outlined"
                                    sx={{
                                      p: 1.25,
                                      opacity: removed ? 0.45 : 1,
                                      height: "100%",
                                      borderColor: removed
                                        ? "divider"
                                        : isGroupStart || isGroupEnd
                                        ? "info.main"
                                        : "divider",
                                      borderWidth: isGroupStart || isGroupEnd ? 2 : 1,
                                    }}
                                  >
                                    <Box
                                      component="img"
                                      src={page.preview_url || page.thumbnail_url}
                                      alt={`Page ${page.page_number}`}
                                      role="button"
                                      tabIndex={0}
                                      onClick={() => setPreviewPage(page)}
                                      onKeyDown={(event) => {
                                        if (event.key === "Enter" || event.key === " ") {
                                          event.preventDefault();
                                          setPreviewPage(page);
                                        }
                                      }}
                                      sx={{
                                        width: "100%",
                                        aspectRatio: "3 / 4",
                                        objectFit: "contain",
                                        backgroundColor: "background.paper",
                                        borderRadius: 1,
                                        border: "1px solid",
                                        borderColor: "divider",
                                        cursor: "zoom-in",
                                        "&:focus-visible": {
                                          outline: "2px solid",
                                          outlineColor: "info.main",
                                          outlineOffset: 2,
                                        },
                                      }}
                                    />
                                    <Divider sx={{ my: 1 }} />
                                    <Box display="flex" flexDirection="column" gap={0.75}>
                                      <Box display="flex" alignItems="center" gap={0.75}>
                                        <Checkbox
                                          checked={!removed}
                                          onChange={(event) => {
                                            saveCurrentLayout();
                                            setRemovedPages((prev) => ({
                                              ...prev,
                                              [page.page_number]: !event.target.checked,
                                            }));
                                          }}
                                          sx={{ p: 0.5 }}
                                        />
                                        <Typography variant="body2" sx={{ flex: 1 }}>
                                          {t("page")} {page.page_number}
                                        </Typography>
                                        {!removed && (
                                          <Chip
                                            size="small"
                                            color="info"
                                            variant="outlined"
                                            label={`${t("document")} ${documentNumber || "-"}`}
                                          />
                                        )}
                                      </Box>
                                      {!removed && canMergeWithNext && (
                                        <FormControlLabel
                                          sx={{
                                            m: 0,
                                            "& .MuiFormControlLabel-label": {
                                              fontSize: "0.78rem",
                                              lineHeight: 1.2,
                                            },
                                          }}
                                          control={
                                            <Switch
                                              size="small"
                                              checked={mergesWithNext}
                                              onChange={(event) => {
                                                saveCurrentLayout();
                                                setMergeWithNextByPage((prev) => ({
                                                  ...prev,
                                                  [page.page_number]: event.target.checked,
                                                }));
                                              }}
                                            />
                                          }
                                          label={t("pdfSplitMergeWithNext")}
                                        />
                                      )}
                                    </Box>
                                  </Card>
                                </Grid>
                              );
                            })}
                          </Grid>
                        </Box>
                      ) : (
                        <Alert severity="info">{t("selectPdfSplitJob")}</Alert>
                      )}
                    </Grid>
                  </Grid>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Dialog
        open={Boolean(previewPage)}
        onClose={() => setPreviewPage(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {t("page")} {previewPage?.page_number}
        </DialogTitle>
        <DialogContent dividers>
          {previewPage && (
            <Box
              component="img"
              src={previewPage.preview_url || previewPage.thumbnail_url}
              alt={`Page ${previewPage.page_number}`}
              sx={{ width: "100%", maxHeight: "70vh", objectFit: "contain", display: "block" }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <MDButton variant="outlined" color="secondary" onClick={() => setPreviewPage(null)}>
            {t("close") || "Close"}
          </MDButton>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(discardTarget)}
        onClose={() => !discarding && setDiscardTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t("pdfSplitDiscard")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {t("pdfSplitDiscardConfirm")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <MDButton
            variant="outlined"
            color="secondary"
            onClick={() => setDiscardTarget(null)}
            disabled={discarding}
          >
            {t("cancel")}
          </MDButton>
          <MDButton
            color="error"
            variant="contained"
            onClick={handleDiscard}
            disabled={discarding}
            aria-label={t("pdfSplitDiscard")}
          >
            {discarding ? <CircularProgress size={18} color="inherit" /> : t("pdfSplitDiscard")}
          </MDButton>
        </DialogActions>
      </Dialog>
      <Footer />
    </DashboardLayout>
  );
};

export default PdfSplitPage;
